/**
 * BotShareModal Component
 * Modal for sharing bots with users or tenants
 * Uses same modal styling as BotModal for consistency
 */

import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import { groupAdminService } from '@modules/group_admin/services/groupAdminService';
import type { QuickShareInfo } from '@modules/group_admin/services/groupAdminService';
import type {
  ShareableUser,
  ShareableTenant,
  ChatbotShare,
  ShareBotPayload,
} from '@modules/group_admin/types';
import './BotModal.css';
import './BotShareModal.css';

// Level-based styling for tenant hierarchy (same as AdminPage)
const levelStyles = [
  { color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.1)', icon: 'crown' },     // Level 0 - Root (purple)
  { color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.1)', icon: 'building' },  // Level 1 - Organization (blue)
  { color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)', icon: 'folder' },    // Level 2 - Department (green)
  { color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.1)', icon: 'users' },     // Level 3 - Team (amber)
  { color: '#ec4899', bgColor: 'rgba(236, 72, 153, 0.1)', icon: 'user' },      // Level 4+ - Sub-team (pink)
];

// Get icon SVG based on level
function getLevelIcon(level: number, color: string) {
  const icons: Record<string, JSX.Element> = {
    crown: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ color }}>
        <path d="M2 20h20v2H2zm2-2L1 7l5 3 6-8 6 8 5-3-3 13H4z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    ),
    building: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ color }}>
        <path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    folder: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ color }}>
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    users: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ color }}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="9" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    user: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ color }}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="12" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  };
  const style = levelStyles[Math.min(level, levelStyles.length - 1)];
  return icons[style.icon] || icons.user;
}

interface BotShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  botId: string;
  botName: string;
}

/** The API returns the raw enum; the badge must not print it.
 *
 *  A value not in this map is NOT quietly rendered as the mildest option — a
 *  badge reading "View only" over an edit grant is a lie about who can change
 *  what. An unknown tier shows the raw value instead, which is ugly on purpose:
 *  it is a visible prompt to extend this map, not a silent downgrade. */
const PERMISSION_LABEL_KEY: Record<string, string> = {
  view: 'botSharing.permView',
  chat: 'botSharing.permChat',
  edit: 'botSharing.permEdit',
};

export const BotShareModal: React.FC<BotShareModalProps> = ({
  isOpen,
  onClose,
  botId,
  botName,
}) => {
  const { t } = useLanguage();
  const { isAdmin, isGroupAdmin, isSuperAdmin } = useAuth();

  const [submitting, setSubmitting] = useState(false);
  const [shareType, setShareType] = useState<'user' | 'tenant' | 'own_tenant' | 'child_tenants'>('user');
  const [users, setUsers] = useState<ShareableUser[]>([]);
  const [tenants, setTenants] = useState<ShareableTenant[]>([]);
  const [existingShares, setExistingShares] = useState<ChatbotShare[]>([]);
  // False until the first listBotShares round-trip, so the panel never claims
  // "not shared with anyone" while the answer is still in flight.
  const [sharesLoaded, setSharesLoaded] = useState(false);

  // Quick share state
  const [quickShareInfo, setQuickShareInfo] = useState<QuickShareInfo | null>(null);
  const [quickShareSubmitting] = useState(false);

  // Share type dropdown state
  const [isShareTypeDropdownOpen, setIsShareTypeDropdownOpen] = useState(false);
  const shareTypeDropdownRef = React.useRef<HTMLDivElement>(null);

  // Form state
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [permissionLevel, setPermissionLevel] = useState<'chat' | 'edit'>('chat');
  const [canReshare, setCanReshare] = useState(false);

  // Edit mode state
  const [editingShareId, setEditingShareId] = useState<string | null>(null);

  // Searchable dropdown state
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [tenantSearchQuery, setTenantSearchQuery] = useState('');
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);
  const userDropdownRef = React.useRef<HTMLDivElement>(null);
  const tenantDropdownRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && botId) {
      setSharesLoaded(false);
      // The list belongs to the entity it was fetched for. Two callers keep
      // this modal MOUNTED between opens and do not reset the entity id
      // (Data Bundles, Skills), so without this the previous item's
      // collaborators stayed on screen under the new item's title for the
      // whole round trip — and a click on one of those rows sent its
      // share_id with the NEW entity's id (caught in review, 2026-09-11).
      setExistingShares([]);
      loadData();
      // Reset form
      setSelectedUserId('');
      setSelectedTenantId('');
      setPermissionLevel('chat');
      setCanReshare(false);
      setShareType('user');
      setUserSearchQuery('');
      setTenantSearchQuery('');
      setIsUserDropdownOpen(false);
      setIsTenantDropdownOpen(false);
      setEditingShareId(null);
    }
  }, [isOpen, botId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
      if (tenantDropdownRef.current && !tenantDropdownRef.current.contains(event.target as Node)) {
        setIsTenantDropdownOpen(false);
      }
      if (shareTypeDropdownRef.current && !shareTypeDropdownRef.current.contains(event.target as Node)) {
        setIsShareTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async () => {
    try {
      const [targets, shares, quickInfo] = await Promise.all([
        groupAdminService.getShareTargets(),
        groupAdminService.listBotShares(botId),
        groupAdminService.getQuickShareInfo(),
      ]);
      setUsers(targets.users);
      setTenants(targets.tenants);
      setExistingShares(shares);
      setQuickShareInfo(quickInfo);
      // Only a fetch that SUCCEEDED may be called loaded: on failure the
      // panel would otherwise say "not shared with anyone yet" about an
      // agent whose shares it never read.
      setSharesLoaded(true);
    } catch (error) {
      console.error('Error loading share data:', error);
      message.error(t('botSharing.loadError') || 'Failed to load sharing data');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Handle quick share types (own_tenant, child_tenants)
    if (shareType === 'own_tenant') {
      if (!quickShareInfo?.own_tenant) return;

      setSubmitting(true);
      try {
        await groupAdminService.shareBot(botId, {
          target_tenant_id: quickShareInfo.own_tenant.tenant_id,
          permission_level: permissionLevel,
          can_reshare: canReshare,
        });
        message.success(t('botSharing.shareToOwnTenantSuccess') || `Shared to ${quickShareInfo.own_tenant.tenant_name}`);
        setPermissionLevel('chat');
        setCanReshare(false);
        loadData();
      } catch (error: any) {
        const errorMsg = error.response?.data?.error || t('sharing.failedOwnTenant');
        if (errorMsg.toLowerCase().includes('already shared')) {
          message.warning(t('botSharing.alreadySharedToOwnTenant') || 'Already shared to this tenant');
        } else {
          message.error(errorMsg);
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (shareType === 'child_tenants') {
      if (!quickShareInfo?.child_tenants?.length) return;

      setSubmitting(true);
      try {
        const result = await groupAdminService.bulkShareBot(botId, {
          target_tenant_ids: quickShareInfo.child_tenants.map(t => t.tenant_id),
          permission_level: permissionLevel,
          can_reshare: canReshare,
        });

        const createdCount = result.created.length;
        const skippedCount = result.skipped.length;
        const failedCount = result.failed.length;

        if (createdCount > 0 && failedCount === 0) {
          message.success(
            t('botSharing.bulkShareSuccess') ||
            t('counts.sharedToTenants', { count: createdCount }) + (skippedCount > 0 ? t('counts.alreadySharedSuffix', { count: skippedCount }) : '')
          );
        } else if (createdCount > 0 && failedCount > 0) {
          message.warning(t('sharing.sharedFailedTenants', { succeeded: createdCount, failed: failedCount }));
        } else if (skippedCount > 0 && createdCount === 0) {
          message.info(t('botSharing.alreadySharedToAllChildren') || 'Already shared to all child tenants');
        } else {
          message.error(t('sharing.shareToChildTenantsFailed'));
        }

        setPermissionLevel('chat');
        setCanReshare(false);
        loadData();
      } catch (error: any) {
        console.error('Bulk share error:', error);
        message.error(error.response?.data?.error || t('sharing.failedChildTenants'));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Validation for regular share types
    if (shareType === 'user' && !selectedUserId) {
      message.error(t('botSharing.userRequired') || 'Please select a user');
      return;
    }
    if (shareType === 'tenant' && !selectedTenantId) {
      message.error(t('botSharing.tenantRequired') || 'Please select a tenant');
      return;
    }

    setSubmitting(true);
    try {
      // If editing, first remove the old share
      if (editingShareId) {
        await groupAdminService.removeBotShare(botId, editingShareId);
      }

      const payload: ShareBotPayload = {
        permission_level: permissionLevel,
        can_reshare: canReshare,
      };

      if (shareType === 'user') {
        payload.target_user_id = selectedUserId;
      } else {
        payload.target_tenant_id = selectedTenantId;
      }

      await groupAdminService.shareBot(botId, payload);
      message.success(
        editingShareId
          ? (t('botSharing.updateSuccess') || 'Share updated successfully')
          : (t('botSharing.shareSuccess') || 'Agent shared successfully')
      );

      // Reset form and reload
      setSelectedUserId('');
      setSelectedTenantId('');
      setPermissionLevel('chat');
      setCanReshare(false);
      setEditingShareId(null);
      loadData();
    } catch (error: any) {
      console.error('Error sharing bot:', error);
      const errorMsg = error.response?.data?.error || t('sharing.failedShareAgent');
      message.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveShare = async (shareId: string) => {
    try {
      await groupAdminService.removeBotShare(botId, shareId);
      message.success(t('botSharing.removeSuccess') || 'Share removed');
      // If we were editing this share, exit edit mode
      if (editingShareId === shareId) {
        setEditingShareId(null);
        resetForm();
      }
      loadData();
    } catch (error) {
      console.error('Error removing share:', error);
      message.error(t('botSharing.removeError') || 'Failed to remove share');
    }
  };

  // Reset form to defaults
  const resetForm = () => {
    setSelectedUserId('');
    setSelectedTenantId('');
    setPermissionLevel('chat');
    setCanReshare(false);
    setShareType('user');
  };

  // Handle edit button click on a share
  const handleEditShare = (share: ChatbotShare) => {
    if (editingShareId === share.share_id) {
      // Toggle off edit mode
      setEditingShareId(null);
      resetForm();
      return;
    }

    // Enter edit mode for this share
    setEditingShareId(share.share_id);

    // Populate form with share data
    if (share.share_type === 'user') {
      setShareType('user');
      setSelectedUserId(share.shared_with_user_id || '');
      setSelectedTenantId('');
    } else {
      setShareType('tenant');
      setSelectedTenantId(share.shared_with_tenant_id || '');
      setSelectedUserId('');
    }

    setPermissionLevel(share.permission_level === 'view' ? 'chat' : share.permission_level as 'chat' | 'edit');
    setCanReshare(share.can_reshare);
  };

  const handleClose = () => {
    if (!submitting && !quickShareSubmitting) {
      onClose();
    }
  };

  // Check if already shared to own tenant
  const isSharedToOwnTenant = () => {
    if (!quickShareInfo?.own_tenant) return false;
    return existingShares.some(
      share => share.share_type === 'tenant' && share.shared_with_tenant_id === quickShareInfo.own_tenant.tenant_id
    );
  };

  // Check how many child tenants are already shared
  const getChildTenantsShareStatus = () => {
    if (!quickShareInfo?.child_tenants?.length) return { shared: 0, total: 0 };
    const sharedCount = quickShareInfo.child_tenants.filter(ct =>
      existingShares.some(share => share.share_type === 'tenant' && share.shared_with_tenant_id === ct.tenant_id)
    ).length;
    return { shared: sharedCount, total: quickShareInfo.child_tenants.length };
  };

  // Get level style for tenant depth
  const getLevelStyle = (depth: number) => {
    return levelStyles[Math.min(depth, levelStyles.length - 1)];
  };

  // Share type options for dropdown
  const getShareTypeOptions = () => {
    const options: Array<{
      id: 'user' | 'tenant' | 'own_tenant' | 'child_tenants';
      label: string;
      description: string;
      icon: JSX.Element;
      disabled?: boolean;
      badge?: string;
    }> = [
      {
        id: 'user',
        label: t('botSharing.shareToUser') || 'Share to User',
        description: t('botSharing.shareToUserDesc') || 'Share to a specific user',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        ),
      },
      {
        id: 'tenant',
        label: t('botSharing.shareToTenant') || 'Share to Tenant',
        description: t('botSharing.shareToTenantDesc') || 'Share to all users in a tenant',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        ),
      },
    ];

    // Add own tenant option for admins
    if (isAdmin && quickShareInfo?.own_tenant) {
      options.push({
        id: 'own_tenant',
        label: t('botSharing.shareToOwnTenant') || 'Share to Own Tenant',
        description: `${quickShareInfo.own_tenant.tenant_name} - ${t('botSharing.allUsersInTenant') || 'All users'}`,
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
          </svg>
        ),
        disabled: isSharedToOwnTenant(),
        badge: isSharedToOwnTenant() ? (t('botSharing.shared') || 'Shared') : undefined,
      });
    }

    // Add child tenants option for group_admin and super_admin
    if ((isGroupAdmin || isSuperAdmin) && quickShareInfo?.child_tenants && quickShareInfo.child_tenants.length > 0) {
      const status = getChildTenantsShareStatus();
      options.push({
        id: 'child_tenants',
        label: t('botSharing.shareToAllChildren', { count: quickShareInfo.child_tenants.length }) || `Share to ${quickShareInfo.child_tenants.length} Child Tenant(s)`,
        description: t('botSharing.shareToAllChildTenantsDesc') || 'Share to all direct child tenants',
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        ),
        disabled: status.shared === status.total,
        badge: status.shared > 0 ? `${status.shared}/${status.total}` : `${status.total}`,
      });
    }

    return options;
  };

  const getSelectedShareTypeOption = () => {
    return getShareTypeOptions().find(opt => opt.id === shareType);
  };

  const handleSelectShareType = (type: 'user' | 'tenant' | 'own_tenant' | 'child_tenants') => {
    setShareType(type);
    setIsShareTypeDropdownOpen(false);
    setSelectedUserId('');
    setSelectedTenantId('');
  };

  // Get selected user/tenant display name
  const getSelectedUserName = () => {
    const user = users.find(u => u.user_id === selectedUserId);
    if (!user) return '';
    return user.user_full_name || user.user_email;
  };

  // Render selected tenant with icon
  const renderSelectedTenant = () => {
    const tenant = tenants.find(t => t.tenant_id === selectedTenantId);
    if (!tenant) return t('botSharing.searchTenant') || 'Search for a tenant...';
    const style = getLevelStyle(tenant.depth);
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {getLevelIcon(tenant.depth, style.color)}
        {tenant.tenant_name}
      </span>
    );
  };

  // Filter users by search query
  const filteredUsers = users.filter(user => {
    const query = userSearchQuery.toLowerCase();
    return (
      (user.user_full_name?.toLowerCase().includes(query) || false) ||
      user.user_email.toLowerCase().includes(query) ||
      (user.tenant_name?.toLowerCase().includes(query) || false)
    );
  });

  // Filter and sort tenants by search query, sorted by depth for hierarchy display
  const filteredTenants = tenants
    .filter(tenant => {
      const query = tenantSearchQuery.toLowerCase();
      return tenant.tenant_name.toLowerCase().includes(query) ||
             tenant.hierarchy_path.toLowerCase().includes(query);
    })
    .sort((a, b) => a.depth - b.depth);

  // Group filtered users by tenant for better UX
  const usersByTenant = filteredUsers.reduce((acc, user) => {
    const tenantName = user.tenant_name || t('sharing.unknownTenant');
    if (!acc[tenantName]) {
      acc[tenantName] = [];
    }
    acc[tenantName].push(user);
    return acc;
  }, {} as Record<string, ShareableUser[]>);

  const handleSelectUser = (userId: string) => {
    setSelectedUserId(userId);
    setIsUserDropdownOpen(false);
    setUserSearchQuery('');
  };

  const handleSelectTenant = (tenantId: string) => {
    setSelectedTenantId(tenantId);
    setIsTenantDropdownOpen(false);
    setTenantSearchQuery('');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-content">
            <h2 className="modal-title">
              <svg className="share-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/>
                <circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
              {t('botSharing.title') || 'Share Bot'}: {botName}
            </h2>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={handleClose}
            aria-label={t('common.close')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
            {/* Share Type Selection - Beautiful Dropdown */}
            <div className="form-group">
              <label className="form-label">
                {t('botSharing.shareType') || 'Share Type'} <span className="required-indicator">*</span>
              </label>
              <div className="share-type-dropdown" ref={shareTypeDropdownRef}>
                <button
                  type="button"
                  className={`share-type-trigger ${isShareTypeDropdownOpen ? 'open' : ''} ${editingShareId ? 'disabled-edit-mode' : ''}`}
                  onClick={() => !editingShareId && setIsShareTypeDropdownOpen(!isShareTypeDropdownOpen)}
                  disabled={submitting || quickShareSubmitting || !!editingShareId}
                >
                  <div className="share-type-trigger-content">
                    <div className="share-type-trigger-icon">
                      {getSelectedShareTypeOption()?.icon}
                    </div>
                    <div className="share-type-trigger-text">
                      <span className="share-type-trigger-label">{getSelectedShareTypeOption()?.label}</span>
                      <span dir="auto" className="share-type-trigger-desc">{getSelectedShareTypeOption()?.description}</span>
                    </div>
                  </div>
                  <svg className="share-type-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6"/>
                  </svg>
                </button>
                {isShareTypeDropdownOpen && (
                  <div className="share-type-options">
                    {getShareTypeOptions().map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={`share-type-option ${shareType === option.id ? 'selected' : ''} ${option.disabled ? 'disabled' : ''}`}
                        onClick={() => !option.disabled && handleSelectShareType(option.id)}
                        disabled={option.disabled}
                      >
                        <div className="share-type-option-icon">
                          {option.icon}
                        </div>
                        <div className="share-type-option-content">
                          <span className="share-type-option-label" dir="auto">{option.label}</span>
                          <span className="share-type-option-desc" dir="auto">{option.description}</span>
                        </div>
                        {option.badge && (
                          <span className={`share-type-badge ${option.disabled ? 'completed' : ''}`} dir="auto">
                            {option.badge}
                          </span>
                        )}
                        {shareType === option.id && (
                          <svg className="share-type-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M20 6L9 17l-5-5"/>
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* User/Tenant Selection - Only show for user/tenant types */}
            {shareType === 'user' && (
              <div className="form-group">
                <label className="form-label">
                  {t('botSharing.selectUser') || 'Select User'} <span className="required-indicator">*</span>
                </label>
                <div className="searchable-select" ref={userDropdownRef}>
                  <button
                    type="button"
                    className={`select-trigger ${selectedUserId ? 'has-value' : ''} ${isUserDropdownOpen ? 'open' : ''} ${editingShareId ? 'disabled-edit-mode' : ''}`}
                    onClick={() => !submitting && !editingShareId && setIsUserDropdownOpen(!isUserDropdownOpen)}
                    disabled={submitting || !!editingShareId}
                  >
                    <span dir="auto" className="select-value">
                      {selectedUserId ? (() => {
                        const u = users.find(x => x.user_id === selectedUserId);
                        if (!u) return getSelectedUserName();
                        return (<>{u.user_full_name || u.user_email}{u.tenant_name && (<span className="user-tenant-tag" dir="auto">{u.tenant_name}</span>)}</>);
                      })() : (t('botSharing.searchUser') || 'Search for a user...')}
                    </span>
                    <svg className="select-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </button>
                  {isUserDropdownOpen && (
                    <div className="select-dropdown">
                      <div className="select-search">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8"/>
                          <path d="M21 21l-4.35-4.35"/>
                        </svg>
                        <input dir="auto"
                          type="text"
                          placeholder={t('botSharing.searchUser') || 'Search users...'}
                          value={userSearchQuery}
                          onChange={(e) => setUserSearchQuery(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="select-options">
                        {Object.entries(usersByTenant).length === 0 ? (
                          <div className="select-no-results">
                            {t('common.noResults') || 'No users found'}
                          </div>
                        ) : (
                          Object.entries(usersByTenant).map(([tenantName, tenantUsers]) => (
                            <div key={tenantName} className="select-group">
                              <div className="select-group-label">{tenantName}</div>
                              {tenantUsers.map((user) => (
                                <button
                                  key={user.user_id}
                                  type="button"
                                  className={`select-option ${selectedUserId === user.user_id ? 'selected' : ''}`}
                                  onClick={() => handleSelectUser(user.user_id)}
                                >
                                  <div className="option-avatar">
                                    {(user.user_full_name || user.user_email).charAt(0).toUpperCase()}
                                  </div>
                                  <div className="option-info">
                                    <span dir="auto" className="option-name">
                                      {user.user_full_name || user.user_email}
                                      {user.tenant_name && (
                                        <span className="user-tenant-tag" dir="auto">{user.tenant_name}</span>
                                      )}
                                    </span>
                                    <span dir="auto" className="option-meta">{user.user_email} · {user.user_role}</span>
                                  </div>
                                  {selectedUserId === user.user_id && (
                                    <svg className="option-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M20 6L9 17l-5-5"/>
                                    </svg>
                                  )}
                                </button>
                              ))}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {shareType === 'tenant' && (
              <div className="form-group">
                <label className="form-label">
                  {t('botSharing.selectTenant') || 'Select Tenant'} <span className="required-indicator">*</span>
                </label>
                <div className="searchable-select" ref={tenantDropdownRef}>
                  <button
                    type="button"
                    className={`select-trigger ${selectedTenantId ? 'has-value' : ''} ${isTenantDropdownOpen ? 'open' : ''} ${editingShareId ? 'disabled-edit-mode' : ''}`}
                    onClick={() => !submitting && !editingShareId && setIsTenantDropdownOpen(!isTenantDropdownOpen)}
                    disabled={submitting || !!editingShareId}
                  >
                    <span dir="auto" className="select-value">
                      {selectedTenantId ? renderSelectedTenant() : (t('botSharing.searchTenant') || 'Search for a tenant...')}
                    </span>
                    <svg className="select-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </button>
                  {isTenantDropdownOpen && (
                    <div className="select-dropdown">
                      <div className="select-search">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8"/>
                          <path d="M21 21l-4.35-4.35"/>
                        </svg>
                        <input dir="auto"
                          type="text"
                          placeholder={t('botSharing.searchTenant') || 'Search tenants...'}
                          value={tenantSearchQuery}
                          onChange={(e) => setTenantSearchQuery(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="select-options">
                        {filteredTenants.length === 0 ? (
                          <div className="select-no-results">
                            {tenants.length === 0
                              ? (t('botSharing.noTenants') || 'No tenants available')
                              : (t('common.noResults') || 'No tenants found')}
                          </div>
                        ) : (
                          filteredTenants.map((tenant) => {
                            const style = getLevelStyle(tenant.depth);
                            return (
                              <button
                                key={tenant.tenant_id}
                                type="button"
                                className={`select-option ${selectedTenantId === tenant.tenant_id ? 'selected' : ''}`}
                                onClick={() => handleSelectTenant(tenant.tenant_id)}
                              >
                                <div className="option-avatar tenant" style={{ background: style.bgColor, border: 'none' }}>
                                  {getLevelIcon(tenant.depth, style.color)}
                                </div>
                                <div className="option-info">
                                  <span dir="auto" className="option-name">
                                    {tenant.tenant_name}
                                    <span className="option-badge" style={{ background: style.bgColor, color: style.color }} dir="auto">
                                      {tenant.is_root_tenant ? 'ROOT' : `Level ${tenant.depth}`}
                                    </span>
                                  </span>
                                  <span className="option-meta" dir="auto">
                                    {tenant.hierarchy_path}
                                  </span>
                                </div>
                                {selectedTenantId === tenant.tenant_id && (
                                  <svg className="option-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 6L9 17l-5-5"/>
                                  </svg>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Permission Level */}
            <div className="form-group">
              <label className="form-label">
                {t('botSharing.permissionLevel') || 'Permission Level'}
              </label>
              <div className="permission-options">
                <label className="permission-option">
                  <input
                    type="radio"
                    name="permission"
                    value="chat"
                    checked={permissionLevel === 'chat'}
                    onChange={() => setPermissionLevel('chat')}
                    disabled={submitting}
                  />
                  <span className="permission-label">{t('botSharing.permChat') || 'Can Chat'}</span>
                </label>
                <label className="permission-option">
                  <input
                    type="radio"
                    name="permission"
                    value="edit"
                    checked={permissionLevel === 'edit'}
                    onChange={() => setPermissionLevel('edit')}
                    disabled={submitting}
                  />
                  <span className="permission-label">{t('botSharing.permEdit') || 'Can Edit (Admin only)'}</span>
                </label>
              </div>
              <p className="form-help-text">{t('botSharing.editRequiresAdmin') || 'Edit permission requires admin role or higher'}</p>
            </div>

            {/* Allow Resharing Toggle */}
            <div className="toggle-row">
              <label className="share-toggle-container">
                <input
                  type="checkbox"
                  checked={canReshare}
                  onChange={(e) => setCanReshare(e.target.checked)}
                  disabled={submitting}
                  className="share-toggle-input"
                />
                <span className="share-toggle-slider"></span>
                <span className="share-toggle-label">{t('botSharing.allowReshare') || 'Allow Resharing'}</span>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={handleClose}
                disabled={submitting}
              >
                {t('common.cancel') || 'Cancel'}
              </button>
              <button type="submit" className={`btn-primary ${editingShareId ? 'btn-edit-mode' : ''}`} disabled={submitting}>
                {submitting ? (
                  <span className="loading-indicator">
                    <div className="loading-spinner"></div>
                    <span>{editingShareId ? (t('common.updating') || 'Updating...') : (t('common.sharing') || 'Sharing...')}</span>
                  </span>
                ) : (
                  <span>{editingShareId ? (t('common.accept') || 'Accept') : (t('botSharing.share') || 'Share')}</span>
                )}
              </button>
            </div>
          </form>

        {/* Existing Shares — always rendered at the same height so the modal
            keeps one shape whether the agent is shared with nobody or with
            twenty people; the list scrolls inside the panel. */}
        <div className="existing-shares-section">
          <div className="existing-shares-title">
            {t('botSharing.existingShares') || 'Current Shares'}
            {sharesLoaded ? (
              <span className="existing-shares-count">{existingShares.length}</span>
            ) : (
              <div className="loading-spinner small"></div>
            )}
          </div>
          <div className="shares-list">
            {sharesLoaded && existingShares.length === 0 && (
              <div className="shares-empty">
                {t('botSharing.noShares') || 'Not shared with anyone yet'}
              </div>
            )}
            {existingShares.map((share) => (
              <div key={share.share_id} className={`share-item ${editingShareId === share.share_id ? 'editing' : ''}`}>
                <div className="share-item-info">
                  <div className="share-item-icon">
                    {share.share_type === 'user' ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                    )}
                  </div>
                  <div className="share-item-details">
                    <span className="share-item-name">
                      {share.share_type === 'user'
                        ? (share.shared_with_user_name || share.shared_with_user_email)
                        : share.shared_with_tenant_name}
                    </span>
                    <div className="share-item-badges">
                      <span className={`share-badge ${share.permission_level}`} dir="auto">
                        {PERMISSION_LABEL_KEY[share.permission_level]
                          ? t(PERMISSION_LABEL_KEY[share.permission_level])
                          : share.permission_level}
                      </span>
                      {share.can_reshare && (
                        <span className="share-badge reshare" dir="auto">{t('botSharing.canReshare')}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="share-item-actions">
                  <button
                    type="button"
                    className={`share-edit-btn ${editingShareId === share.share_id ? 'active' : ''}`}
                    onClick={() => handleEditShare(share)}
                    title={editingShareId === share.share_id ? (t('common.cancelEdit') || 'Cancel edit') : (t('common.edit') || 'Edit')}
                  >
                    {editingShareId === share.share_id ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    className="share-remove-btn"
                    onClick={() => handleRemoveShare(share.share_id)}
                    title={t('common.remove') || 'Remove share'}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BotShareModal;
